import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useNiotextStore } from '@/store/modules/niotext/store.ts';
import { useConversationDisplayUserResolver } from '@/hooks/useConversationDisplayUserResolver.ts';
import type ConversationDisplayUser from '@/entities/ConversationDisplayUser.ts';

export enum ConversationMemberType {
    USER = 'user',
    LEAD = 'lead',
}

export interface ConversationUserMember {
    type: ConversationMemberType.USER;
    id: number;
    fullName: string;
    avatarUrl: string | null;
}

export interface ConversationLeadMember {
    type: ConversationMemberType.LEAD;
    id: number;
    fullName: string;
    avatarUrl: string | null;
}

export type ConversationMember = ConversationUserMember | ConversationLeadMember;

export function useConversationMembers() {
    const { resolveUsers } = useConversationDisplayUserResolver();
    const niotextStore = useNiotextStore();
    const { selectedConversation } = storeToRefs(niotextStore);

    const conversationUsers = computed<ConversationDisplayUser[]>(() => {
        if (!selectedConversation.value?.conversationUserIds) {
            return [];
        }

        return resolveUsers(selectedConversation.value.conversationUserIds);
    });

    const conversationMembers = computed<ConversationMember[]>(() => {
        const members: ConversationMember[] = conversationUsers.value.map((user) => ({
            type: ConversationMemberType.USER,
            id: user.id,
            fullName: user.fullName,
            avatarUrl: user.avatarUrl,
        }));

        const lead = selectedConversation.value?.lead;

        if (lead) {
            members.push({
                type: ConversationMemberType.LEAD,
                id: lead.id,
                fullName: lead.fullName,
                avatarUrl: null,
            });
        }

        return members;
    });

    return { conversationUsers, conversationMembers };
}
